import { defineStore } from "pinia";
import { Administration } from "./entity";
import { AdministrationList } from "./list.gen";
import type { AdministrationStore, AdministrationStoreState } from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";
import request from "@/data/api/request";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const administrationList = new AdministrationList();
export const useAdministrationStore = defineStore("administrationStore", {
  state: (): AdministrationStoreState => ({
    activeID: null,
    cache: {},
    list: [] as Administration[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
    loading: false,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-Administration", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.Administration"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(administration: Administration): Promise<void> {
      // Set active
      await administration
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = administration.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(administration: Administration): void {
      const id = administration.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(administration);

        // Set active
        if (this.active === null) {
          this.activeID = administration.id;
        }
      } else {
        this.reinsert(administration);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as Administration[]).map((x: any) => {
        const administration = new Administration({});
        administration.set(x as JSONData);
        return administration;
      });
    },
    async delete(administration: Administration): Promise<void> {
      await administration
        .delete()
        .then(() => {
          this.remove(administration);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const administration = (this.list as Administration[]).find((x: Administration) => x.id === id);
      if (administration !== undefined) {
        await this.delete(administration);
      } else {
        throw new Error("Could not find Administration with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as Administration[]).some((x: Administration) => x.id === id);
    },
    get(id: string): Administration | null {
      return (this.list as Administration[]).find((x: Administration) => x.id === id) || null;
    },
    reinsert(administration: Administration): void {
      const index = (this.list as Administration[]).findIndex(
        (x: Administration) => x.id === administration.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          administration,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(administration);
      }
      this.updateKey += 1;
    },
    remove({ id }: Administration): void {
      const index = (this.list as Administration[]).findIndex((x: Administration) => x.id === id);
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<Administration> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const administration = this.get(id);
        if (administration !== null) {
          return administration;
        }
      }

      // Retrieve administration
      const administration = new Administration({});

      // Get administration
      await administration.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate administration
        await this.activate(entity);
      });

      return administration;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: Administration[];
    }> {
      // Check if a refresh is required
      this.loading = true;
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as Administration[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await administrationList.getAll().then(async (list) => {
        list.forEach((administration) => {
          this.reinsert(administration);
        });

        // Activate administration
        if (list.length > 0) {
          if (administrationList.active !== null) {
            this.activeID = administrationList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("administration_id", this.activeID);
      }

      this.loading = false;

      return {
        active: this.activeID,
        items: this.list as Administration[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: Administration[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as Administration),
        };
      }

      // Retrieve list
      const items = await administrationList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((administration) => {
            this.reinsert(administration);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: Administration) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: Administration[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as Administration),
        };
      }

      // Retrieve list
      const items = await administrationList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((administration) => {
            this.reinsert(administration);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: Administration) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: Administration[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as Administration),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await administrationList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((administration) => {
            this.reinsert(administration);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: Administration) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(administration: Administration): Promise<string> {
      // Update administration

      if(administration.id === 'new'){
        // If the administration is new, we need to set a new ID
        administration.generateId();
      }

      await administration.update();

      // Reinsert administration
      this.reinsert(administration);

      // Activate administration
      if (this.active === null) {
        await this.activate(administration);
      }

      this.emitStoreUpdateEvent();

      return administration.id;
    },
    /**
     * Generate a new bearer token for an administration
     */
    async generateBearerToken(id: string): Promise<{ bearerToken: string }> {
      const response = await request.post(`/administration/${id}/generate-bearer-token`);
      return response.data;
    },
  },
  getters: {
    active(): Administration | null {
      if (this.activeID === null) {
        return null;
      }

      const administration = (this.list as Administration[]).find(
        (x: Administration) => x.id === this.activeID,
      );
      if (administration === undefined) {
        return null;
      }

      return administration;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createAdministrationStore(): AdministrationStore {
  const store = useAdministrationStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as AdministrationStore; // TODO: Fix this
}

